/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/shared/validateContributorSectionRows.ts.
 */

import type { FormField, FormSubSection } from "../../../../../interfaces/formInterfaces";
import {
  buildSectionFieldKey,
  type ContributorFormErrors,
  fieldLabel,
  getFirstFormError,
  hasFieldValue,
} from "../shared/contributorFormValidation";

export type { ContributorFormErrors };
export { getFirstFormError };

/** Type definition — Validate Section Rows Input. */
export type ValidateSectionRowsInput = {
  sections: FormSubSection[];
  sectionRows: Record<string, number>;
  values: Record<string, string>;
  shouldSkipField?: (
    field: FormField,
    sectionCode: string,
    rowIndex: number
  ) => boolean;
  getRowCount?: (sectionCode: string, defaultRowCount: number) => number;
  /** Override value presence check (e.g. UNIT fields with CMS unit ids). */
  hasFieldValue?: (
    field: FormField,
    key: string,
    values: Record<string, string>
  ) => boolean;
};

/** Exported function — validate Contributor Section Rows. */
export function validateContributorSectionRows({
  sections,
  sectionRows,
  values,
  shouldSkipField,
  getRowCount,
  hasFieldValue: hasFieldValueOverride,
}: ValidateSectionRowsInput): ContributorFormErrors {
  const errors: ContributorFormErrors = {};

  for (const section of sections) {
    const code = section.code || section.name || String(section.id);
    const defaultRowCount = sectionRows[code] || 1;
    const rowCount = getRowCount
      ? getRowCount(code, defaultRowCount)
      : defaultRowCount;

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      for (const field of section.fields) {
        if (!field.isEnabled || !field.isMandatory) continue;
        if (shouldSkipField?.(field, code, rowIndex)) continue;

        const key = buildSectionFieldKey(field.name, code, rowIndex);
        const present = hasFieldValueOverride
          ? hasFieldValueOverride(field, key, values)
          : hasFieldValue(values[key]);
        if (!present && !errors[key]) {
          errors[key] = `${fieldLabel(field)} is required.`;
        }
      }
    }
  }

  return errors;
}
